import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Image, Type, Store, Globe } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { PageContainer } from '@/components/layout/PageContainer'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { StorefrontSettingsGuard } from './StorefrontSettingsGuard'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type { TenantSettingsResponse } from '@/lib/api/controlPlaneClient'

export function BrandingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { settings, isLoading, updateAsync, isUpdating, queryKey } =
    useStorefrontTenantSettings()

  const [storeName, setStoreName] = useState('')
  const [siteIconUrl, setSiteIconUrl] = useState('')
  const [ogImageUrl, setOgImageUrl] = useState('')
  const [ogDescription, setOgDescription] = useState('')

  useEffect(() => {
    if (settings) {
      setStoreName(settings.store_name ?? '')
      setSiteIconUrl(settings.site_icon_url ?? '')
      setOgImageUrl(settings.og_image_url ?? '')
      setOgDescription(settings.og_description ?? '')
    }
  }, [settings])

  const handleSave = async () => {
    try {
      await updateAsync({
        store_name: storeName.trim(),
        site_icon_url: siteIconUrl.trim(),
        og_image_url: ogImageUrl.trim(),
        og_description: ogDescription.trim(),
      })
      toast.success('Branding settings saved')
    } catch {
      toast.error('Failed to save branding settings')
    }
  }

  const hasChanges =
    settings &&
    (storeName.trim() !== (settings.store_name ?? '') ||
      siteIconUrl.trim() !== (settings.site_icon_url ?? '') ||
      ogImageUrl.trim() !== (settings.og_image_url ?? '') ||
      ogDescription.trim() !== (settings.og_description ?? ''))

  return (
    <StorefrontSettingsGuard>
      <PageContainer>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => navigate('/storefront/settings')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-2xl font-semibold">Branding</h2>
              <p className="text-sm text-muted-foreground">
                Store name, link preview image and description
              </p>
            </div>
          </div>

          {isLoading ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-5 w-40 mt-4" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Store className="w-4 h-4 text-muted-foreground" />
                    Store Name
                  </label>
                  <Input
                    placeholder="My Store"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Displayed in the browser tab title. Leave empty to use your
                    company name.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    Site Icon (Favicon)
                  </label>
                  <Input
                    placeholder="https://cdn.example.com/favicon.png"
                    value={siteIconUrl}
                    onChange={(e) => setSiteIconUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Displayed in the browser tab. Recommended: square PNG or SVG,
                    at least 192 x 192 pixels. Leave empty to use the default
                    Zaviago starburst icon.
                  </p>
                  {siteIconUrl.trim() && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg border flex items-center justify-center overflow-hidden bg-muted">
                        <img
                          src={siteIconUrl.trim()}
                          alt="Favicon preview"
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            ;(e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">Preview</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Image className="w-4 h-4 text-muted-foreground" />
                    OG Image URL
                  </label>
                  <Input
                    placeholder="https://cdn.example.com/og-image.jpg"
                    value={ogImageUrl}
                    onChange={(e) => setOgImageUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Image shown when your store link is shared on LINE, Facebook,
                    etc. Recommended size: 1200 x 630 pixels. Leave empty to use
                    your site icon.
                  </p>
                  {ogImageUrl.trim() && (
                    <div className="mt-3 rounded-lg border overflow-hidden max-w-sm">
                      <img
                        src={ogImageUrl.trim()}
                        alt="OG preview"
                        className="w-full h-auto object-cover"
                        onError={(e) => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Type className="w-4 h-4 text-muted-foreground" />
                    OG Description
                  </label>
                  <Textarea
                    placeholder="Your store description for link previews"
                    value={ogDescription}
                    onChange={(e) => setOgDescription(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Short description shown in link previews. Leave empty for
                    auto-generated description.
                  </p>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <label className="text-sm font-medium">
                      Hide &quot;powered by Zaviago&quot;
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Remove &quot;powered by Zaviago&quot; from auto-generated
                      link preview descriptions (LINE, Facebook, etc.)
                    </p>
                  </div>
                  {isLoading ? (
                    <Skeleton className="w-9 h-5 rounded-full" />
                  ) : (
                    <Switch
                      checked={!!settings?.hide_powered_by}
                      onCheckedChange={async (checked) => {
                        const previousData = queryClient.getQueryData<TenantSettingsResponse>(queryKey)
                        queryClient.setQueryData<TenantSettingsResponse | undefined>(
                          queryKey,
                          (old) => old ? { ...old, hide_powered_by: checked ? 1 : 0 } : old,
                        )
                        try {
                          await updateAsync({ hide_powered_by: checked ? 1 : 0 })
                          toast('Setting saved.')
                        } catch {
                          queryClient.setQueryData(queryKey, previousData)
                          toast.error('Failed to save. Please try again.')
                        }
                      }}
                      disabled={isUpdating}
                      aria-label="Hide powered by Zaviago from link previews"
                    />
                  )}
                </div>

                <Button
                  onClick={handleSave}
                  disabled={isUpdating || !hasChanges}
                  className="w-full sm:w-auto"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </PageContainer>
    </StorefrontSettingsGuard>
  )
}
